import React, { useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, getDoc, serverTimestamp, orderBy, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject as deleteFile } from 'firebase/storage';
import { Box, Typography, Card, CardContent, TextField, Button, List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton, Grid, Chip, ToggleButtonGroup, ToggleButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import styles from '../styles/Dashboard.module.css';

const GerenciarPrestadores = () => {
  const [prestadores, setPrestadores] = useState([]);
  const [novoPrestador, setNovoPrestador] = useState({ nome: '', documento: '', tipo_servico: '', horario_inicio: '', horario_fim: '' });
  const [diasSemana, setDiasSemana] = useState([]);
  const [foto, setFoto] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [currentUserData, setCurrentUserData] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [isAcessoPontual, setIsAcessoPontual] = useState(false);
  const [dataPontual, setDataPontual] = useState(null);

  // Estados para o modal de edição
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPrestador, setSelectedPrestador] = useState(null);
  const [editFormData, setEditFormData] = useState({ nome: '', documento: '', tipo_servico: '', horario_inicio: '', horario_fim: '', dias_semana: [], data_pontual: null });
  const [editFoto, setEditFoto] = useState(null);
  const dias = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setCurrentUserData(userData);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'tb_prestadores_servico_morador'), where('morador_id', '==', currentUser.uid), orderBy('nome', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPrestadores(list);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleCreatePrestador = async (e) => {
    e.preventDefault();
    if (!novoPrestador.nome || !novoPrestador.tipo_servico) {
      alert('Nome e Tipo de Serviço são obrigatórios.');
      return;
    }
    setUploading(true);
    try {
      let foto_url = '';
      let foto_path = '';
      if (foto) {
        const storage = getStorage();
        foto_path = `prestadores_servico_morador/${condominioId}/${currentUser.uid}_${Date.now()}_${foto.name}`;
        const fotoRef = ref(storage, foto_path);
        const metadata = { customMetadata: { 'ownerId': currentUser.uid, 'condominioId': condominioId } };
        await uploadBytes(fotoRef, foto, metadata);
        foto_url = await getDownloadURL(fotoRef);
      }

      await addDoc(collection(db, 'tb_prestadores_servico_morador'), {
        ...novoPrestador,
        tipo_acesso: isAcessoPontual ? 'pontual' : 'recorrente',
        dias_semana: !isAcessoPontual ? diasSemana : [],
        data_pontual: isAcessoPontual ? dataPontual : null,
        morador_id: currentUser.uid,
        morador_nome: currentUserData.nome,
        morador_apto: currentUserData.apto,
        condominio_id: condominioId,
        foto_url,
        foto_path,
        created_at: serverTimestamp(),
      });

      setNovoPrestador({ nome: '', documento: '', tipo_servico: '', horario_inicio: '', horario_fim: '' });
      setIsAcessoPontual(false);
      setDataPontual(null);
      setDiasSemana([]);
      setFoto(null);
      alert('Prestador de serviço adicionado com sucesso!');
    } catch (error) {
      alert('Erro ao adicionar prestador: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const openEditDialog = (prestador) => {
    setSelectedPrestador(prestador);
    setEditFormData({
      nome: prestador.nome,
      documento: prestador.documento || '',
      tipo_servico: prestador.tipo_servico,
      horario_inicio: prestador.horario_inicio || '',
      horario_fim: prestador.horario_fim || '',
      dias_semana: prestador.dias_semana || [],
      data_pontual: prestador.data_pontual ? prestador.data_pontual.toDate() : null,
    });
    setEditDialog(true);
  };

  const handleUpdatePrestador = async (e) => {
    e.preventDefault();
    if (!selectedPrestador) return;

    setUploading(true);
    try {
      let { foto_url, foto_path } = selectedPrestador;

      // Se uma nova foto foi selecionada, processa o upload
      if (editFoto) {
        const storage = getStorage();
        // Deleta a foto antiga, se existir
        if (foto_path) {
          const oldFotoRef = ref(storage, foto_path);
          await deleteFile(oldFotoRef).catch(err => console.warn("Foto antiga não encontrada para deletar:", err));
        }
        // Faz upload da nova foto
        const newFotoPath = `prestadores_servico_morador/${condominioId}/${currentUser.uid}_${Date.now()}_${editFoto.name}`;
        const newFotoRef = ref(storage, newFotoPath);
        const metadata = { customMetadata: { 'ownerId': currentUser.uid, 'condominioId': condominioId } };
        await uploadBytes(newFotoRef, editFoto, metadata);
        foto_url = await getDownloadURL(newFotoRef);
        foto_path = newFotoPath;
      }

      const prestadorRef = doc(db, 'tb_prestadores_servico_morador', selectedPrestador.id);
      await updateDoc(prestadorRef, {
        ...editFormData,
        tipo_acesso: editFormData.data_pontual ? 'pontual' : 'recorrente',
        dias_semana: editFormData.data_pontual ? [] : editFormData.dias_semana,
        foto_url,
        foto_path,
      });

      setEditDialog(false);
      setEditFoto(null);
      setSelectedPrestador(null);
      alert('Prestador atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar prestador: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialog(false);
    setSelectedPrestador(null);
    setEditFoto(null);
    setEditFormData({ nome: '', documento: '', tipo_servico: '', horario_inicio: '', horario_fim: '', dias_semana: [], data_pontual: null });
  };

  const handleDeletePrestador = async (prestador) => {
    if (!window.confirm(`Tem certeza que deseja remover ${prestador.nome}?`)) return;
    try {
      if (prestador.foto_path) {
        const storage = getStorage();
        const fotoRef = ref(storage, prestador.foto_path);
        await deleteFile(fotoRef);
      }
      await deleteDoc(doc(db, 'tb_prestadores_servico_morador', prestador.id));
      alert('Prestador removido com sucesso.');
    } catch (error) {
      alert('Erro ao remover prestador: ' + error.message);
    }
  };

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 3 }, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh', overflowX: 'hidden' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Meus Prestadores de Serviço</Typography>

        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Adicionar Novo Prestador</Typography>
            <Grid container spacing={2} component="form" onSubmit={handleCreatePrestador}>
              <Grid item xs={12} md={4}><TextField fullWidth label="Nome Completo" value={novoPrestador.nome} onChange={(e) => setNovoPrestador({ ...novoPrestador, nome: e.target.value })} required /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Documento (CPF/RG)" value={novoPrestador.documento} onChange={(e) => setNovoPrestador({ ...novoPrestador, documento: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Tipo de Serviço (Ex: Diarista)" value={novoPrestador.tipo_servico} onChange={(e) => setNovoPrestador({ ...novoPrestador, tipo_servico: e.target.value })} required /></Grid>

              <Grid item xs={12} md={6}>
                {!foto ? (
                  <Button variant="outlined" component="label" fullWidth>Anexar Foto
                    <input type="file" hidden accept="image/*" onChange={(e) => setFoto(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip label={foto.name} onDelete={() => setFoto(null)} />
                )}
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FormControlLabel
                  control={<Switch checked={isAcessoPontual} onChange={(e) => setIsAcessoPontual(e.target.checked)} />}
                  label={isAcessoPontual ? "Acesso Pontual" : "Acesso Recorrente"}
                />
              </Grid>

              {!isAcessoPontual ? (
                <>
                  <Grid item xs={12}><Typography variant="subtitle2">Dias de Acesso</Typography>
                    <ToggleButtonGroup value={diasSemana} onChange={(e, newDias) => setDiasSemana(newDias)}>{dias.map(dia => <ToggleButton key={dia} value={dia}>{dia}</ToggleButton>)}</ToggleButtonGroup>
                  </Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Horário de Início" type="time" value={novoPrestador.horario_inicio} onChange={(e) => setNovoPrestador({ ...novoPrestador, horario_inicio: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Horário de Fim" type="time" value={novoPrestador.horario_fim} onChange={(e) => setNovoPrestador({ ...novoPrestador, horario_fim: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={4}><DatePicker label="Data do Acesso" value={dataPontual} onChange={setDataPontual} sx={{ width: '100%' }} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Horário de Início" type="time" value={novoPrestador.horario_inicio} onChange={(e) => setNovoPrestador({ ...novoPrestador, horario_inicio: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Horário de Fim" type="time" value={novoPrestador.horario_fim} onChange={(e) => setNovoPrestador({ ...novoPrestador, horario_fim: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
                </>
              )}

              <Grid item xs={12}>
                <Button type="submit" variant="contained" size="large" disabled={uploading}>
                  {uploading ? <CircularProgress size={24} /> : 'Adicionar Prestador'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Meus Prestadores Cadastrados</Typography>
            <List>
              {prestadores.map((p) => (
                <ListItem key={p.id} divider secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" onClick={() => openEditDialog(p)}><EditIcon /></IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeletePrestador(p)} sx={{ ml: 1 }}><DeleteIcon /></IconButton>
                  </>
                }>
                  <ListItemAvatar><Avatar src={p.foto_url}><PersonIcon /></Avatar></ListItemAvatar>
                  <ListItemText
                    primary={`${p.nome} - ${p.tipo_servico}`}
                    secondary={
                      p.tipo_acesso === 'pontual' ?
                        `Acesso Pontual: ${p.data_pontual ? format(p.data_pontual.toDate(), 'dd/MM/yyyy') : 'N/A'} | Horário: ${p.horario_inicio || 'N/A'} - ${p.horario_fim || 'N/A'}` :
                        `Acesso Recorrente: ${p.dias_semana?.join(', ') || 'Nenhum dia'} | Horário: ${p.horario_inicio || 'N/A'} - ${p.horario_fim || 'N/A'}`
                    }
                  />
                </ListItem>
              ))}
              {prestadores.length === 0 && <Typography sx={{ p: 2 }}>Nenhum prestador cadastrado.</Typography>}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Modal de Edição */}
      <Dialog open={editDialog} onClose={handleCloseEditDialog} PaperProps={{ component: 'form', onSubmit: handleUpdatePrestador }}>
        <DialogTitle>Editar Prestador de Serviço</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nome Completo" type="text" fullWidth variant="standard" value={editFormData.nome} onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })} required />
          <TextField margin="dense" label="Documento (CPF/RG)" type="text" fullWidth variant="standard" value={editFormData.documento} onChange={(e) => setEditFormData({ ...editFormData, documento: e.target.value })} />
          <TextField margin="dense" label="Tipo de Serviço" type="text" fullWidth variant="standard" value={editFormData.tipo_servico} onChange={(e) => setEditFormData({ ...editFormData, tipo_servico: e.target.value })} required />

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={!!editFormData.data_pontual} onChange={(e) => setEditFormData({ ...editFormData, data_pontual: e.target.checked ? new Date() : null, dias_semana: [] })} />}
              label={editFormData.data_pontual ? "Acesso Pontual" : "Acesso Recorrente"}
            />
          </Box>

          {editFormData.data_pontual ? (
            <DatePicker label="Data do Acesso" value={editFormData.data_pontual} onChange={(newDate) => setEditFormData({ ...editFormData, data_pontual: newDate })} sx={{ width: '100%', mt: 1 }} />
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Dias de Acesso</Typography>
              <ToggleButtonGroup value={editFormData.dias_semana} onChange={(e, newDias) => setEditFormData({ ...editFormData, dias_semana: newDias })} size="small">
                {dias.map(dia => <ToggleButton key={dia} value={dia}>{dia}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
          )}
          <TextField margin="dense" label="Horário de Início" type="time" fullWidth variant="standard" value={editFormData.horario_inicio} onChange={(e) => setEditFormData({ ...editFormData, horario_inicio: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" label="Horário de Fim" type="time" fullWidth variant="standard" value={editFormData.horario_fim} onChange={(e) => setEditFormData({ ...editFormData, horario_fim: e.target.value })} InputLabelProps={{ shrink: true }} />
          <Box sx={{ mt: 2 }}>
            {!editFoto ? (
              <Button variant="outlined" component="label" fullWidth>
                Alterar Foto
                <input type="file" hidden accept="image/*" onChange={(e) => setEditFoto(e.target.files[0])} />
              </Button>
            ) : (
              <Chip label={editFoto.name} onDelete={() => setEditFoto(null)} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={uploading}>
            {uploading ? <CircularProgress size={24} /> : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default GerenciarPrestadores;
